import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { 
  Bell, 
  Send, 
  CheckCircle2, 
  XCircle, 
  Calendar,
  Clock,
  Mail,
  AlertTriangle,
  RefreshCw,
  History
} from "lucide-react";

export default function AdminPaymentReminders() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/reminder-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/reminder-logs?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs);
      setLogsTotal(logsRes.data.total);
    } catch (error) {
      toast.error("Failed to load reminder data");
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/send-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { total_sent, reminders_7_days, reminders_3_days } = response.data;
      
      if (total_sent === 0) {
        toast.info("No payment reminders needed at this time");
      } else {
        toast.success(`${total_sent} reminder(s) sent successfully!`);
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-payment-reminders-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
                <Bell className="w-8 h-8" />
                Payment Reminders
              </h1>
              <p className="text-text-muted mt-1">Manage and track payment reminder notifications</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={fetchData}
                disabled={loading}
                data-testid="refresh-reminders-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleSendReminders}
                disabled={sending || loading}
                className="bg-primary hover:bg-primary-800"
                data-testid="send-reminders-btn"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Reminders Now
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">{stats.upcoming_payments_7_days}</p>
                      <p className="text-xs text-text-muted">Due in 7 Days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Bell className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">{stats.total_reminders_sent}</p>
                      <p className="text-xs text-text-muted">Total Sent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-600">{stats.successful_emails}</p>
                      <p className="text-xs text-text-muted">Successful</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{stats.failed_emails}</p>
                      <p className="text-xs text-text-muted">Failed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">{stats.reminders_sent_today}</p>
                      <p className="text-xs text-text-muted">Sent Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Info Banner */}
          <Card className="border-amber-200 bg-amber-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Automatic Reminders Enabled</p>
                  <p className="text-xs text-amber-700 mt-1">
                    The system automatically checks for upcoming payments daily at 8:00 AM UTC and sends reminders 
                    to your email ({stats?.sent_to || 'admin'}) for payments due in 7 days and 3 days.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reminder Logs */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Reminder Log ({logsTotal})
              </CardTitle>
              <CardDescription>
                History of all payment reminders sent
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No reminders sent yet</p>
                  <p className="text-sm text-stone-400 mt-1">
                    Click "Send Reminders Now" to check for upcoming payments
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tenant</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Days Before</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Sent At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} data-testid={`reminder-log-${log.id}`}>
                          <TableCell className="font-medium">{log.tenant_name}</TableCell>
                          <TableCell className="text-sm">
                            {log.location} - Apt #{log.apartment_number}
                          </TableCell>
                          <TableCell className="font-medium text-green-600">
                            ₦{log.payment_amount}
                          </TableCell>
                          <TableCell>{formatDate(log.payment_due_date)}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={log.days_before === 3 ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}
                            >
                              {log.days_before} days
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline"
                              className={log.trigger_type === 'manual' ? 'border-blue-300 text-blue-700' : 'border-stone-300 text-stone-600'}
                            >
                              {log.trigger_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {log.email_sent ? (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Sent
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-text-muted">
                            {formatDateTime(log.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
